import type {
  UserManagerSettings,
  SilentRenewErrorCallback
} from 'oidc-client-ts';

import AuthService from './AuthService';

/* eslint-disable @typescript-eslint/naming-convention */
const oidcConfig: Omit<
  UserManagerSettings,
  'client_id' | 'authority' | 'scope'
> = {
  redirect_uri: `${__APP_URL__}/signin-oidc`,
  post_logout_redirect_uri: __APP_URL__,
  response_mode: 'fragment',
  response_type: 'code',
  loadUserInfo: true,
  automaticSilentRenew: true,
  silent_redirect_uri: `${__APP_URL__}/renew.html`,
  revokeTokensOnSignout: true,
  filterProtocolClaims: false,
  acr_values: ''
};

type AuthOptions = Partial<UserManagerSettings> &
  Pick<UserManagerSettings, 'client_id' | 'authority' | 'scope'> & {
    onSilentRenewError: SilentRenewErrorCallback;
    onAccessTokenExpired: () => void;
  };
/* eslint-enable @typescript-eslint/naming-convention */

let authService: AuthService;
let silentRenewErrorCb: SilentRenewErrorCallback;
let accessTokenExpiredCallback: () => void;

export type GUID = `${string}-${string}-${string}-${string}-${string}`;
export function isValidGuid(tenantId: string | undefined): tenantId is GUID {
  if (!tenantId) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    tenantId
  );
}

export default {
  initialize(options: AuthOptions, acrValues: string) {
    oidcConfig.acr_values = acrValues;
    authService = new AuthService({
      ...oidcConfig,
      ...options
    });

    silentRenewErrorCb = options.onSilentRenewError;
    accessTokenExpiredCallback = options.onAccessTokenExpired;

    authService.events.addSilentRenewError(silentRenewErrorCb);
    authService.events.addAccessTokenExpired(accessTokenExpiredCallback);
    authService.events.addAccessTokenExpiring(accessTokenExpiredCallback)
  },
  removeCallbacks() {
    authService.events.removeSilentRenewError(silentRenewErrorCb);
  },
  login() {
    authService.login();
  },
  loginWhenPageVisible() {
    if (document.hidden) {
      document.addEventListener(
        'visibilitychange',
        () => {
          // force login when user returns to the page
          // If we redirect to login and the page isn't visible, the login on Volue Identity can time out
          authService.login();
        },
        { once: true }
      );
    } else {
      authService.login();
    }
  },
  logout() {
    authService.logout();
  },
  async getName() {
    const user = await authService.getUserData();
    const names = user?.profile.name;
    if (typeof names === 'string') {
      return names;
    }
    if (Array.isArray(names)) {
      return names[0];
    }
    return undefined;
  },

  async getIdentityProvider() {
    const user = await authService.getUserData();
    const idp = user?.profile?.idp as string;
    return idp;
  },

  async getPreferredUsername() {
    const user = await authService.getUserData();
    return user?.profile?.preferred_username;
  },
  async getUserId() {
    const user = await authService.getUserData();
    return user?.profile?.sub;
  },

  async getToken() {
    if (authService.isCallback()) {
      const user = await authService.completeLogin();
      return user.access_token;
    }

    const user = await authService.getUserData();
    if (user && !user.expired) {
      return user.access_token;
    }

    if(user?.expired) {
      const user = await authService.refreshToken();
      return (user)?.access_token;
    }
  },

  async hasTokenExpired() {
    const user = await authService.getUserData();
    return user?.expired;
  },

  async refreshToken() {
    const user = await authService.refreshToken();
    return (user)?.access_token;
  },

  async getTenants() {
    const user = await authService.getUserData();
    if (user && !user.expired) {
      const tenants = (user?.profile?.tenants as string).split(',');
      return tenants as GUID[];
    }
    return undefined;
  }
};
